import { MotionValue } from "motion/react";
import {
  NEW_LOOK_PULL_MAX_DISTANCE,
  NEW_LOOK_PULL_RESISTANCE,
  NEW_LOOK_REFRESH_LOADING_DISTANCE,
  NEW_LOOK_REFRESH_THRESHOLD,
  PULL_TO_REFRESH_MIN_DURATION,
  PULL_TO_REFRESH_NO_REPORT_TIMEOUT,
  PULL_TO_REFRESH_SLOP,
} from "common/constants";

export type PullDirection = "pull" | "up" | "sideways";

export interface PullModel {
  threshold: number;
  loadingDistance: number;
  stiffness: number;
  damping: number;
  shrinksOnFinish: boolean;
  resist: (travel: number) => number;
}

export interface RefreshSpinnerProps {
  distance: MotionValue<number>;
  finish: MotionValue<number>;
  isRefreshing: boolean;
  model: PullModel;
}

export const NEW_LOOK_PULL_MODEL: PullModel = {
  threshold: NEW_LOOK_REFRESH_THRESHOLD,
  loadingDistance: NEW_LOOK_REFRESH_LOADING_DISTANCE,
  stiffness: 320,
  damping: 36,
  shrinksOnFinish: true,
  resist: (travel) =>
    NEW_LOOK_PULL_MAX_DISTANCE *
    (1 - Math.exp(-Math.max(0, travel) / NEW_LOOK_PULL_RESISTANCE)),
};

export const getPullDirection = (
  deltaX: number,
  deltaY: number
): PullDirection | undefined => {
  const isSideways =
    Math.abs(deltaX) > PULL_TO_REFRESH_SLOP &&
    Math.abs(deltaX) >= Math.abs(deltaY);
  if (isSideways) return "sideways";
  if (deltaY > PULL_TO_REFRESH_SLOP) return "pull";
  if (deltaY < -PULL_TO_REFRESH_SLOP) return "up";
  return undefined;
};

export const shouldHoldTouch = (
  direction: PullDirection | undefined,
  deltaX: number,
  deltaY: number
) => {
  if (deltaY <= 0) return false;
  return direction === "pull" || (!direction && deltaY > Math.abs(deltaX));
};

export const getPullDistance = (deltaY: number, model: PullModel) =>
  model.resist(deltaY - PULL_TO_REFRESH_SLOP);

export const getPullProgress = (distance: number, model: PullModel) =>
  Math.min(1, Math.max(0, distance / model.threshold));

export const shouldRefreshOnRelease = (distance: number, model: PullModel) =>
  distance >= model.threshold;

export const getRefreshFinishDelay = (
  elapsed: number,
  sawRefreshing: boolean
) =>
  Math.max(
    0,
    PULL_TO_REFRESH_MIN_DURATION - elapsed,
    sawRefreshing ? 0 : PULL_TO_REFRESH_NO_REPORT_TIMEOUT - elapsed
  );
